'use client'

import { GridSection } from '@/components/marketing/fragments/grid-section'
import { AnimatedGridPattern } from '@/components/ui/animated-grid-pattern'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import * as React from 'react'
import { SiteHeading } from '../fragments/site-heading'

export function PrivacyPolicy(): React.JSX.Element {
  const { i18n } = useLingui()

  return (
    <div className='relative'>
      <AnimatedGridPattern />
      <GridSection>
        <div className='bg-diagonal-lines'>
          <div className='flex flex-col bg-background md:px-12 px-4 py-20 md:py-24 lg:mx-12 lg:border-x overflow-hidden'>
            <SiteHeading badge={msg`Legal`} title={msg`Privacy Policy`} />

            <div className='prose max-w-5xl text-justify mx-auto text-muted-foreground text-sm sm:text-sm md:text-base'>
              <p>
                {i18n._(
                  'Pione Joint Stock Company is committed to protecting your privacy when using our websites, platforms, applications, and services, including but not limited to PIONE Wallet, PIONE Farm, PIONE House, PIONE Mart, PIONE Swap, PIONE Care, PIONE Chain, and other products and services based on blockchain or integrated with artificial intelligence (AI). This privacy policy explains how we collect, use, store, and protect your information. By using the Services, you agree to the policies stated in this document.'
                )}
              </p>

              <h2 className='text-xl font-bold mt-8 mb-4'>{i18n._('1. Information We Collect')}</h2>
              <p>
                {i18n._(
                  'We may collect the following types of information when you use the Services:'
                )}
              </p>
              <ul className='list-disc pl-6'>
                <li>
                  {i18n._(
                    'Personal Information: Includes name, email address, phone number, or other details you provide when creating an account or contacting us.'
                  )}
                </li>
                <li>
                  {i18n._(
                    'Blockchain Data: Public wallet address and blockchain transaction history, which are publicly stored on blockchain networks.'
                  )}
                </li>
                <li>
                  {i18n._(
                    'Usage Data: Information about how you interact with the Services, such as IP address, device type, operating system, and activity on the platform.'
                  )}
                </li>
              </ul>

              <h2 className='text-xl font-bold mt-8 mb-4'>
                {i18n._('2. Purpose of Information Use')}
              </h2>
              <p>{i18n._('We use the collected information to:')}</p>
              <ul className='list-disc pl-6'>
                <li>
                  {i18n._(
                    'Provide and improve the Services, including transaction processing, customer support, and enhancing user experience.'
                  )}
                </li>
                <li>
                  {i18n._(
                    'Ensure security and legal compliance, such as fraud detection or responding to authority requests.'
                  )}
                </li>
                <li>
                  {i18n._(
                    'Send notifications, updates, or marketing information (if you have consented to receive them).'
                  )}
                </li>
              </ul>

              <h2 className='text-xl font-bold mt-8 mb-4'>{i18n._('3. Information Sharing')}</h2>
              <p>
                {i18n._(
                  'We do not sell or rent your personal information. Information may be shared under the following circumstances:'
                )}
              </p>
              <ul className='list-disc pl-6'>
                <li>
                  {i18n._(
                    'With third-party service providers that support the operation of the Services, such as data hosting or payment processing, provided they comply with strict security measures.'
                  )}
                </li>
                <li>
                  {i18n._(
                    'When required by law, authorities, or to protect the legitimate interests of Pione Group.'
                  )}
                </li>
                <li>
                  {i18n._(
                    'Public blockchain data (such as wallet addresses) is inherently available on blockchain networks and is not considered personal information.'
                  )}
                </li>
              </ul>

              <h2 className='text-xl font-bold mt-8 mb-4'>
                {i18n._('4. Data Security and Storage')}
              </h2>
              <p>
                {i18n._(
                  'We use modern encryption technologies and advanced security measures, including blockchain, to protect your personal information. Data is stored as long as necessary to provide the Services or as required by law. However, no system is completely secure, and you should protect your login credentials.'
                )}
              </p>

              <h2 className='text-xl font-bold mt-8 mb-4'>{i18n._('5. User Rights')}</h2>
              <p>{i18n._('You have the following rights regarding your personal information:')}</p>
              <ul className='list-disc pl-6'>
                <li>
                  {i18n._(
                    'Access and Correction: Request to view or update your personal information.'
                  )}
                </li>
                <li>
                  {i18n._(
                    'Deletion: Request deletion of personal information unless retention is legally required.'
                  )}
                </li>
                <li>
                  {i18n._(
                    'Marketing Opt-out: Unsubscribe from receiving marketing communications at any time.'
                  )}
                </li>
              </ul>

              <h2 className='text-xl font-bold mt-8 mb-4'>
                {i18n._('6. Changes to the Privacy Policy')}
              </h2>
              <p>
                {i18n._(
                  'We may update this Privacy Policy at any time. Changes will take effect immediately upon posting on the website or notification through the Services. Your continued use of the Services after the changes indicates your acceptance of the new policy.'
                )}
              </p>
            </div>
          </div>
        </div>
      </GridSection>
    </div>
  )
}
